<template>
  <div
    :class="[
      'widget-step amount-step',
      { 'active': isActiveStep }
    ]"
    data-test-id="fill-amount"
  >
    <StepTitle
      :stepNumber="stepNumber"
      @click.native="$emit('setStep', stepNumber)"
    >
      <div
        class="widget-step__title"
        data-test-id="fill-amount-title"
      >
        <div>
          {{ i18n.amount_step_title() }}
          <md-button
            v-show="selectedAsset && showTooltip"
            class="md-icon-button md-raised widget-step__tooltip-button"
            data-test-id="fill-amount-tooltip-btn"
          >
            <md-icon
              md-src="/static/img/info-icon.svg"
              data-test-id="fill-amount-tooltip-btn-icon"
            />
            <md-tooltip
              md-direction="bottom"
              class="min-withdrawal-tooltip"
              data-test-id="min-withdrawal-tooltip"
            >
              <p
                v-if="hasMinAmount"
                data-test-id="min-withdrawal-tooltip-min-msg"
              >
                {{ commissionDescription }}
              </p>
              <div
                v-for="(item, key) in limitList"
                :key="key"
              >
                <template v-if="item.period === 'operation' || item.period === 'day'">
                  <p
                    v-if="item.period === 'operation'"
                    data-test-id="min-withdrawal-tooltip-operation-msg"
                  >
                    {{
                      i18n.lim_withdrawal_daily_msg1({
                        limit: item.amount_limit,
                        asset: selectedAsset
                      })
                    }}
                  </p>
                  <p
                    v-if="item.period === 'day'"
                    data-test-id="min-withdrawal-tooltip-day-msg"
                  >
                    {{
                      i18n.lim_withdrawal_daily_msg2({
                        limit: item.amount_limit,
                        asset: selectedAsset
                      })
                    }}
                  </p>
                </template>
              </div>
              <p
                class="border-none"
                data-test-id="min-withdrawal-tooltip-holiday-msg"
              >
                {{ i18n.lim_withdrawal_daily_msg3() }}
              </p>
            </md-tooltip>
          </md-button>
        </div>
        <p
          v-show="amount && !amount.includes('NaN') && !isActiveStep"
          data-test-id="selected-amount"
        >
          {{ amount }}
        </p>
      </div>
    </StepTitle>

    <AccordeonWrapper v-if="isActiveStep">
      <div
        class="widget-step__inner"
        data-test-id="fill-amount-container"
      >
        <input-field
          class="field__number"
          data-test-id="fill-amount-input"
          v-model.trim="amount"
          label=""
          name="amount"
          type="number"
          title="Amount"
          align="right"
          vvValidateOn="change"
          v-validate="'required|amount'"
          :errorMessage="amountFieldErrors"
          :autofocus="true"
          @blur="onBlurHandler"
        />
        <span
          class="validation-error"
          data-test-id="fill-amount-input-error"
        >
          {{ amountFieldErrors ? amountFieldErrors : '' }}
        </span>
        <div
          :class="[
            'fees-container',
            { 'loading': isFeesLoadPending }
          ]"
        >
          <div
            v-if="amount && amount > 0 && !amountFieldErrors"
            data-test-id="fill-amount-fees"
          >
            <p data-test-id="fill-amount-fixed-fee">
              {{ feesFixedMsg }}
            </p>

            <p
              v-if="isFeesCharged"
              data-test-id="fill-amount-charged"
            >
              {{ willChargedMsg }}
            </p>
          </div>
        </div>
      </div>
    </AccordeonWrapper>
  </div>
</template>

<script>
  import { i18n } from '@js/i18n'
  import get from 'lodash/get'

  import { mapGetters } from 'vuex'
  import { vuexTypes } from '@vuex/types'

  import formMixin from '@com/mixins/form.mixin'
  import CurrencyRound from '@com/mixins/currency.round.mixin'

  import StepTitle from '@com/UI/StepTitle.vue'
  import AccordeonWrapper from '@com/UI/AccordeonWrapper.vue'
  import InputField from '@com/fields/InputField'

  export default {
    name: 'FillAmount',
    mixins: [formMixin, CurrencyRound],
    components: {
      StepTitle,
      AccordeonWrapper,
      InputField
    },
    props: {
      stepNumber: {
        type: [Number, String],
        required: true
      },
      isActiveStep: {
        type: Boolean,
        default: false
      },
      selectedAsset: {
        type: String,
        default: ''
      },
      minAmount: {
        type: [Number, String],
        default: null
      },
      limitList: {
        type: Array,
        required: true
      },
      curentSelectedTocken: {
        type: String,
        default: ''
      },
      isFeesLoadPending: {
        type: Boolean,
        required: true
      },
      allFees: {
        type: Object,
        required: true
      },
      willCharged: {
        type: String,
        required: true
      },
      showTooltip: {
        type: Boolean,
        required: true
      }
    },
    emits: [
      'setStep',
      'onFieldBlur',
      'setAmount',
      'setFeesInZero',
      'setAmountFieldError'
    ],
    data () {
      return {
        i18n,
        amount: ''
      }
    },
    computed: {
      ...mapGetters([
        vuexTypes.accountBalances
      ]),
      lessThenMinimumAmount () {
        return this.hasMinAmount && this.amount !== ''
          ? Number(this.amount) < this.minAmount
          : false
      },
      hasMinAmount () {
        return this.minAmount !== null &&
          this.minAmount !== undefined &&
          this.minAmount !== ''
      },
      commissionDescription () {
        if (!this.hasMinAmount) return ''
        return i18n.withdraw_how_much({
          asset: this.selectedAsset,
          value: this.minAmount
        })
      },
      balance () {
        return this.accountBalances[this.curentSelectedTocken]
      },
      isLimitExceeded () {
        return Number(this.amount) > Number(get(this.balance, 'balance') || 0)
      },
      amountFieldErrors () {
        return this.errors.first('amount') ||
          (this.isLimitExceeded ? i18n.withdraw_error_insufficient_funds() : '') ||
          (this.lessThenMinimumAmount
            ? i18n.withdraw_error_minimum_amount({
              value: this.minAmount,
              asset: this.selectedAsset
            })
            : '')
      },
      isFeesCharged () {
        const minAmount = this.hasMinAmount ? parseFloat(this.minAmount) : 0
        return parseFloat(this.amount) >= minAmount &&
          !this.isLimitExceeded && this.willCharged > 0
      },
      feesFixedMsg () {
        return i18n.fees_percent_fixed({
          percent: this.allFees.percent,
          fixed: this.allFees.fixed,
          currency: this.selectedAsset
        })
      },
      willChargedMsg () {
        return i18n.fees_will_charged({
          willCharged: this.willCharged,
          currency: this.selectedAsset
        })
      }
    },
    methods: {
      onBlurHandler () {
        this.$emit('onFieldBlur')
        this.checkAmount()
      },
      checkAmount () {
        this.amount = this.roundCurrency2(this.amount, this.curentSelectedTocken)
      }
    },
    watch: {
      amount (value) {
        if (this.isLimitExceeded || !+this.amount) return

        if (value === '' || (this.hasMinAmount && +value < +this.minAmount)) {
          this.$emit('setFeesInZero')
          return
        }
        this.$emit('setAmount', value)
      },
      amountFieldErrors (value) {
        this.$emit('setAmountFieldError', !!value)
      }
    }
  }
</script>

<style lang="scss">
@import '~scss/variables';

.amount-step {
  &.widget-step {
    .widget-step__tooltip-button {
      background: $color-graphite !important;
      width: 30px;
      height: 30px;
      min-width: 30px;
      flex-shrink: 1;

      .md-icon {
        width: 20px;
        min-width: 20px;
        height: 20px;
        flex-shrink: 1;
        color: $color-mint !important;
        font-size: 16px !important;

        svg {
          fill: $color-mint !important;
        }
      }

      &_danger {
        background: $col-error !important;
        color: $default-light-white !important;

        .md-icon svg {
          fill: $default-light-white !important;
        }
      }
    }

    .widget-step__inner {
      .fees-container {
        font-size: 14px;
        min-height: 40px;

        &.loading {
          opacity: 0.3;
        }
      }
    }
  }
}

.md-tooltip.min-withdrawal-tooltip {
  font-size: 15px;
  border-radius: 6px;
  padding: 6px;
  display: flex;
  flex-direction: column;
  max-width: 400px;
  width: 100%;
  white-space: initial;
  height: max-content;

  p {
    border-bottom: 1px solid #fff;
    padding-bottom: 6px;
    margin-bottom: 6px;

    &.border-none {
      border-bottom: none;
      margin-bottom: 0px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/app/dashboard/widgets/WithdrawalWidget/FillAmount.vue